import { useState } from 'react'
import spec from '../../../../shared/imaging-requirements.json'

const FULL_GUIDE_URL = 'https://www.osteo3d.com/imaging-requirements'
const CHECK_SCAN_URL = 'https://www.osteo3d.com/check-scan'

/**
 * "Before you upload" scan requirements, shown on the public upload page.
 *
 * This page is the one surface a scan centre reliably opens — the case link is
 * forwarded from Osteo3d to the doctor or patient and on to the imaging centre by
 * WhatsApp, and the radiographer usually sees nothing else from us. Anything the
 * data has to satisfy needs to be visible here, before the files are picked.
 *
 * Content comes from `shared/imaging-requirements.json`, the same source the public
 * /imaging-requirements page and the printable console card render from, so the
 * three can never drift apart.
 */
export default function ScanRequirementsCard() {
  const [open, setOpen] = useState(false)
  const { items, zeroDoseNote } = spec.quickReference

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">Before you upload — what makes a scan usable</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {open ? 'Tap to hide' : 'Sending the wrong series is the most common reason a case is delayed. Tap to check.'}
          </p>
        </div>
        <svg
          className={`flex-shrink-0 w-4 h-4 text-gray-400 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          <ul className="space-y-3">
            {items.map((item, i) => (
              <li key={item.heading} className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">{item.heading}</p>
                  <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>

          <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 leading-relaxed">
            {zeroDoseNote}
          </p>

          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5">
            <p className="text-[11px] font-semibold text-gray-700 mb-1.5">We cannot use</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              {spec.doNotSend.items.slice(0, 6).map((d) => d.item).join(' · ')}
            </p>
          </div>

          {/* The checker comes first: whoever opens this link has usually already scanned,
              so "is what I have usable?" is a more immediate question than the protocol. */}
          <a
            href={CHECK_SCAN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 hover:bg-blue-100 transition-colors"
          >
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-blue-900">
                Check this scan before you upload it
              </span>
              <span className="block text-[11px] text-blue-700 mt-0.5">
                Runs in your browser — nothing is uploaded, no patient data leaves your computer.
              </span>
            </span>
            <span aria-hidden="true" className="text-blue-600 flex-shrink-0">→</span>
          </a>

          <a
            href={FULL_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Full imaging protocol, export steps and scanner-specific notes
            <span aria-hidden="true">→</span>
          </a>
        </div>
      )}
    </div>
  )
}
